import React, { useState, useEffect, useRef, useMemo } from "react"
import { useQuery, useQueryClient } from "react-query"
import { fetchDeals, fetchOpenPositions } from "../../services/main.service.ts"
import Paginator from "../composites/paginator.tsx"
import WithAuth from "../hoc/with-auth.tsx"
import { LayoutWithSidebar } from "../layouts/layout-with-sidebar.tsx/index.tsx"
import {
  Badge,
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Grid,
  GridItem,
  Button,
  Input,
  IconButton,
  Link as CLink,
  Tabs,
  TabList,
  Tab,
} from "@chakra-ui/react"
import _ from "lodash"
import BlockedLoader from "../composites/blocked-loader.tsx"
import DateRangePicker from "../composites/date-range-picker.tsx"
import { AiOutlineSearch } from "react-icons/ai"
import { InputWrapper } from "../composites/index.tsx"
import { ReactSelect } from "../composites/select.tsx"
import { SelectSymbols } from "../composites/select-symbols.tsx"
import { CSVLink, CSVDownload } from "react-csv"
import { AiOutlineDownload } from "react-icons/ai"
import { getTradesByIPService } from "../../services/sql.service.ts"
import { AgGridReact } from "ag-grid-react"
import { formatDate } from "../../utils/formatDate.ts"
import { getIpAddressService } from "../../services/crm.service.ts"
import { FaDownload } from "react-icons/fa"
import moment from "moment"
import csvDownload from "json-to-csv-export"

// const captions = [
//   "Symbol",
//   "Ticket",
//   "Login",
//   "DeaL",
//   "Type",
//   "Volume",
//   "S/L",
//   "T/P",
//   "Time",
// ]

const captions = [
  "IP Addresss",
  "Deal",
  "Position",
  "Login",
  "Name",
  "Time",
  "Entry",
  "Symbol",
  "Volume",
  "Price",
  "S/L",
  "T/P",
  "Reason",
  "Profit",
  "Dealer",
  "Comment",
]

const TradesByIPPage = () => {
  const [isSetForcedUpdate, setIsForcedUpdate] = useState(false)
  const [year, setyear] = useState("2023")
  const [currentPage, setCurrentpage] = useState(1)
  const [tabIndex, setTabIndex] = useState(0)

  const [isDownloading, setIsDownloading] = useState(false)

  const [[startDate, endDate], setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([new Date(), new Date()])

  const [loginId, setLoginId] = useState("")
  const [symbolStr, setSymbolStr] = useState<string>("")
  const [finalState, setFinalState] = useState<{
    year: string
    loginId: string
    symbolStr: string
    startDate: Date | undefined
    endDate: Date | undefined
  }>({
    year: year,
    loginId: loginId,
    symbolStr: symbolStr,
    startDate: startDate,
    endDate: endDate,
  })

  const updateFinalState = () => {
    setFinalState({
      year: year,
      loginId,
      symbolStr,
      startDate,
      endDate,
    })
  }

  const {
    data,
    isLoading,
    isError,
    isRefetching,
    isFetched,
    isFetchedAfterMount,
    refetch,
  } = useQuery(
    [
      "trades-by-ip",
      {
        currentPage,
        finalState,
      },
    ],
    () =>
      getIpAddressService(
        startDate ? formatDate(startDate) : "",
        endDate ? formatDate(endDate) : startDate ? formatDate(startDate) : ""
      ),
    {
      refetchInterval: 15000,
    }
  )

  const tradesbyIpAddress = useMemo(() => {
    let _trades: any[] = []

    if (data?.data?.data?.length) {
      const ts = _.filter(data?.data?.data, (_d) => {
        return _d?.message?.includes("order placed") ? true : false
      })

      _trades = _.map(ts, (_t) => {
        let arry = extractContentBetween(_t?.message, "#", "]").split(" ")

        return {
          ..._t,
          login: extractContentBetween(_t?.message, "'", "'"),
          orderId: arry[0],
          action: arry[1],
          volume: arry[3],
          symbol: arry[4],
          price: parseFloat(arry[6]).toFixed(2) ?? arry[6],
          date: moment.unix(parseInt(_t?.time)).format("DD-MM-YYYY HH:mm:ss"),
        }
      })

      let groupedTrades = _.groupBy(_trades, tabIndex === 0 ? "login" : "ip")

      _trades = _.map(groupedTrades, (_value, _key) => {
        return {
          login: tabIndex === 0 ? _key : _.map(_value, "login").join(","),
          ip: tabIndex === 0 ? _.map(_value, "ip").join(",") : _key,
          orderId: _.map(_value, "orderId").join(","),
          action: _.map(_value, "action").join(","),
          volume: _.map(_value, "volume").join(","),
          symbol: _.map(_value, "symbol").join(","),
          price: _.map(_value, "price").join(","),
          date: _.map(_value, "date").join(","),
        }
      })
    }

    return _trades
  }, [data, tabIndex])

  useEffect(() => {
    if (isFetchedAfterMount) {
      setIsForcedUpdate(true)
    }
  }, [currentPage, year])

  useEffect(() => {
    if (!isFetchedAfterMount) {
      if (!isRefetching) {
        if (isSetForcedUpdate) {
          setIsForcedUpdate(false)
        }
      }
    }
  }, [isRefetching])

  const gridRef = useRef<any>() // Optional - for accessing Grid's API
  const [rowData, setRowData] = useState() // Set rowData to Array of Objects, one Object per Row

  const cellRendererFunc = (params: any) => (
    <>
      {_.map(params.value.split(","), (_v) => {
        return (
          <>
            {_v}
            <br />
          </>
        )
      })}
    </>
  )

  // login orderId action volume symbol price date ip
  const [columnDefs, setColumnDefs] = useState([
    { headerName: "Login", field: "login", cellRenderer: cellRendererFunc },
    { headerName: "IP Address", field: "ip", cellRenderer: cellRendererFunc },
    {
      headerName: "Order Id",
      field: "orderId",
      cellRenderer: cellRendererFunc,
    },
    { headerName: "Action", field: "action", cellRenderer: cellRendererFunc },
    { headerName: "Volume", field: "volume", cellRenderer: cellRendererFunc },
    { headerName: "Symbol", field: "symbol", cellRenderer: cellRendererFunc },
    { headerName: "Price", field: "price", cellRenderer: cellRendererFunc },
    { headerName: "Date", field: "date", cellRenderer: cellRendererFunc },
  ])

  const [columnDefsByIp, setColumnDefsByIP] = useState([
    { headerName: "IP Address", field: "ip", cellRenderer: cellRendererFunc },
    { headerName: "Login", field: "login", cellRenderer: cellRendererFunc },
    {
      headerName: "Order Id",
      field: "orderId",
      cellRenderer: cellRendererFunc,
    },

    { headerName: "Action", field: "action", cellRenderer: cellRendererFunc },
    { headerName: "Volume", field: "volume", cellRenderer: cellRendererFunc },
    { headerName: "Symbol", field: "symbol", cellRenderer: cellRendererFunc },
    { headerName: "Price", field: "price", cellRenderer: cellRendererFunc },
    { headerName: "Date", field: "date", cellRenderer: cellRendererFunc },
  ])

  // DefaultColDef sets props common to all Columns
  const defaultColDef = useMemo(
    () => ({
      // make every column editable
      editable: false,
      selectable: true,
      // make every column use 'text' filter by default
      filter: "agTextColumnFilter",
      // enable floating filters by default
      floatingFilter: true,
      // make columns resizable
      resizable: true,
      // disable cell data types
      cellDataType: false,
      sortable: true,
    }),
    []
  )

  const onDownloadCSV = () => {
    setIsDownloading(true)

    let _trades: any[] = []

    if (data?.data?.data?.length) {
      const ts = _.filter(data?.data?.data, (_d) => {
        return _d?.message?.includes("order placed") ? true : false
      })

      _trades = _.map(ts, (_t) => {
        let arry = extractContentBetween(_t?.message, "#", "]").split(" ")

        let t = {
          ..._t,
          login: extractContentBetween(_t?.message, "'", "'"),
          orderId: arry[0],
          action: arry[1],
          volume: arry[3],
          symbol: arry[4],
          price: arry[6],
          date: moment.unix(parseInt(_t?.time)).format("DD-MM-YYYY HH:mm:ss"),
        }

        delete t["message"]
        delete t["time"]

        return t
      })

      _trades = _.orderBy(_trades, tabIndex === 0 ? "login" : "ip")

      csvDownload({
        data: _trades,
        filename: "trades-by-ip",
        delimiter: ",",
        headers: [
          "IP Address",
          "Login",
          "Order ID",
          "Action",
          "Volume",
          "Symbol",
          "Price",
          "Time",
        ],
      })
    }

    setIsDownloading(false)
  }

  return (
    <>
      <Box my={4}>
        <Tabs
          index={tabIndex}
          onChange={(index: any) => {
            setTabIndex(index)
          }}
        >
          <TabList>
            <Tab>Show by Login</Tab>
            <Tab>Show By IP Address</Tab>
          </TabList>
        </Tabs>
      </Box>
      <Grid
        templateColumns={[
          "repeat(1, 1fr)",
          "repeat(2, 1fr)",
          "repeat(3, 1fr)",
          "repeat(4, 1fr)",
          "repeat(5, 1fr)",
        ]}
        gap={6}
      >
        <Box position="relative">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            setDateRange={setDateRange}
          />
        </Box>

        <Box>
          <Flex align="center">
            <Box pt={[0, "35px"]} pr={4}>
              <Button
                colorScheme="blue"
                onClick={() => {
                  updateFinalState()
                }}
                cursor="pointer"
              >
                <Box as={AiOutlineSearch} mr={2} />
                Search
              </Button>
            </Box>

            <Box pt={[0, "35px"]} pr={4}>
              <Button
                colorScheme="green"
                onClick={() => {
                  onDownloadCSV()
                }}
                cursor="pointer"
              >
                <Box as={FaDownload} mr={2} />
                Download
              </Button>
            </Box>
          </Flex>
        </Box>
      </Grid>

      <div className="ag-grid-container">
        <div
          className="ag-theme-alpine"
          style={{ width: "100%", height: "calc(100vh - 100px)" }}
        >
          <AgGridReact
            suppressScrollOnNewData={true}
            ref={gridRef} // Ref for accessing Grid's API
            rowData={tradesbyIpAddress ?? []} // Row Data for Rows
            columnDefs={tabIndex === 0 ? columnDefs : columnDefsByIp} // Column Defs for Columns
            defaultColDef={defaultColDef} // Default Column Properties
            animateRows={true} // Optional - set to 'true' to have rows animate when sorted
            overlayNoRowsTemplate={
              isLoading ? "Loading orders..." : "No  orders found"
            }
            domLayout="autoHeight"
            getRowHeight={(params: any) => {
              if (tabIndex === 1) {
                return params?.data?.login?.split(",")?.length
                  ? params?.data?.login?.split(",")?.length * 40
                  : 40
              }

              return params?.data?.ip?.split(",")?.length
                ? params?.data?.ip?.split(",")?.length * 40
                : 40
            }}
          />
        </div>
      </div>
    </>
  )
}

export default TradesByIPPage

function extractContentBetween(
  inputString: string,
  startChar: string,
  endChar: string
) {
  const regex = new RegExp(`${startChar}(.*?)${endChar}`)
  const match = regex.exec(inputString)
  if (match) {
    let result = match[0]
    result = result.slice(startChar.length, -endChar.length)
    return result
  }

  return ""
}
